/**
 * The "Tell {agent}" composer's state — one small pure reducer, the same machine on web, iOS and Android
 * (`native-apps/shared/outreach-composer/contract.json` `composer.transitions`).
 *
 *   idle ──send──▶ sending ──2xx──▶ sentPending
 *                          └─error─▶ failedKeepsDraft ──send──▶ sending (same clientNoteId)
 *                                                     └─edit──▶ idle (clientNoteId dropped)
 *   sentPending ──edit──▶ idle (a new draft; the pending note and its refetch are unaffected)
 *
 * The client never sends to the contact: a send POSTs the owner's words to `/api/tell-agent-*`, which
 * records the note and hands it to the owner's own agent. Text only — no attachments, no dictation (R20).
 *
 * No runtime imports, so the jest suite can load it directly (orion-web CLAUDE.md "Tests (jest)").
 */

export type ComposerPhase = 'idle' | 'sending' | 'sentPending' | 'failedKeepsDraft';

/** The note a successful send produced — what the optimistic TOLD/PENDING item is built from. */
export interface SentNote {
  noteId: string;
  conversationId: string;
  text: string;
  /** ISO — the local send time, which places the optimistic item until the server's own event arrives. */
  at: string;
}

export interface ComposerState {
  phase: ComposerPhase;
  /** The draft. Kept through `sending` (the field is disabled) and through a failure. */
  text: string;
  /** Minted once per draft on its first send, kept across retries of the same text. */
  clientNoteId: string | null;
  /** The last note that landed, or null before the first. */
  sent: SentNote | null;
}

export type ComposerEvent =
  | { type: 'edit'; text: string }
  /** `mintId` is called only when the draft has no clientNoteId yet. */
  | { type: 'send'; mintId: () => string }
  | { type: 'success'; noteId: string; conversationId: string; at: string }
  | { type: 'error' };

export const COMPOSER_MAX_CHARS = 4000;

/** The person-scoped re-read after a send: +3 s, +10 s, +30 s from the send, on ONE owned timer. */
export const REFETCH_AFTER_MS: readonly number[] = [3000, 10000, 30000];

export const initialComposerState: ComposerState = { phase: 'idle', text: '', clientNoteId: null, sent: null };

/** Send is enabled for 1–4000 trimmed characters while not already sending. */
export function canSendComposer(state: ComposerState): boolean {
  const length = state.text.trim().length;
  return state.phase !== 'sending' && length >= 1 && length <= COMPOSER_MAX_CHARS;
}

export function composerReducer(state: ComposerState, event: ComposerEvent): ComposerState {
  switch (event.type) {
    case 'edit': {
      // The field is disabled while a send is in flight; an edit arriving then changes nothing.
      if (state.phase === 'sending' || event.text === state.text) return state;
      if (state.phase === 'failedKeepsDraft') return { ...state, phase: 'idle', text: event.text, clientNoteId: null };
      if (state.phase === 'sentPending') return { ...state, phase: 'idle', text: event.text };
      return { ...state, text: event.text };
    }
    case 'send': {
      if (!canSendComposer(state)) return state;
      return { ...state, phase: 'sending', clientNoteId: state.clientNoteId ?? event.mintId() };
    }
    case 'success': {
      if (state.phase !== 'sending') return state;
      return {
        phase: 'sentPending',
        text: '',
        clientNoteId: null,
        sent: { noteId: event.noteId, conversationId: event.conversationId, text: state.text.trim(), at: event.at },
      };
    }
    case 'error': {
      if (state.phase !== 'sending') return state;
      return { ...state, phase: 'failedKeepsDraft' };
    }
  }
}

/** The body the endpoint takes — the trimmed draft, its clientNoteId and the surface's channel. */
export function composerRequestBody(
  state: ComposerState,
  scope: { kind: 'thread'; outreachId: string } | { kind: 'contact'; personId: string },
  channel: 'WEB' | 'APP',
): Record<string, string> | null {
  if (state.phase !== 'sending' || !state.clientNoteId) return null;
  const target: Record<string, string> = scope.kind === 'thread' ? { outreachId: scope.outreachId } : { personId: scope.personId };
  return { ...target, text: state.text.trim(), clientNoteId: state.clientNoteId, channel };
}

/** A 2xx body carries `{noteId, conversationId}`; anything else is not a landed note. */
export function parseTellAgentResponse(body: unknown): { noteId: string; conversationId: string } | null {
  if (!body || typeof body !== 'object') return null;
  const { noteId, conversationId } = body as { noteId?: unknown; conversationId?: unknown };
  if (typeof noteId !== 'string' || !noteId.trim()) return null;
  return { noteId, conversationId: typeof conversationId === 'string' ? conversationId : '' };
}

/**
 * The refetch schedule's next step: the delay before the next re-read, or null when it is over — the
 * schedule ran out, or the note already settled (thread scope: SENT / HANDLED / FAILED; contact scope:
 * the note landed on a thread). `elapsedSteps` is how many re-reads have already run.
 */
export function nextRefetchDelay(elapsedSteps: number, settled: boolean): number | null {
  if (settled || elapsedSteps >= REFETCH_AFTER_MS.length) return null;
  const previous = elapsedSteps === 0 ? 0 : REFETCH_AFTER_MS[elapsedSteps - 1];
  return REFETCH_AFTER_MS[elapsedSteps] - previous;
}

/** A v4 UUID, lowercase — `crypto.randomUUID` where the runtime has it. */
export function mintClientNoteId(): string {
  const cryptoApi = (globalThis as { crypto?: { randomUUID?: () => string; getRandomValues?: (a: Uint8Array) => Uint8Array } }).crypto;
  if (cryptoApi?.randomUUID) return cryptoApi.randomUUID().toLowerCase();
  const bytes = new Uint8Array(16);
  if (cryptoApi?.getRandomValues) cryptoApi.getRandomValues(bytes);
  else for (let i = 0; i < 16; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
